/**
 * MobileNav Component - Bottom tab bar navigation for mobile devices
 *
 * This component provides:
 * - Fixed bottom navigation bar (visible only on mobile)
 * - Touch-friendly tab buttons with icons and labels
 * - Active state indication with brand color
 * - Safe area padding for notched phones
 */

import { useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { Home, FolderOpen, Scissors, Settings, LogOut, Search } from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import clsx from 'clsx'
import { useAuth } from '../../contexts/auth'
import GlobalSearch from '../GlobalSearch'

const MobileNav = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const { logout } = useAuth()
  const [showSearch, setShowSearch] = useState(false)

  const handleLogout = async () => {
    try {
      await logout()
      navigate('/login')
    } catch (error) {
      console.error('Logout failed:', error)
    }
  }

  const navItems: Array<{ path: string; label: string; icon: LucideIcon; disabled?: boolean }> = [
    { path: '/', label: 'Home', icon: Home },
    { path: '/projects', label: 'Assets', icon: FolderOpen },
    { path: '/assemblies', label: 'Assemblies', icon: Scissors },
    { path: '/system', label: 'System', icon: Settings },
  ]

  return (
    <>
      {/* Mobile search overlay */}
      {showSearch && (
        <div className="fixed inset-0 z-[60] md:hidden bg-[var(--rq-bg)]/95 backdrop-blur-sm">
          <GlobalSearch isCollapsed={false} isMobile onClose={() => setShowSearch(false)} />
          <button
            onClick={() => setShowSearch(false)}
            className="absolute top-3 right-4 text-[var(--rq-text-soft)] hover:text-[var(--rq-text)]"
          >
            <span className="text-xs">Cancel</span>
          </button>
        </div>
      )}

    <nav className="fixed bottom-0 left-0 right-0 mobile-nav-shell md:hidden z-50 safe-area-bottom">
      <div className="flex items-center justify-around h-16">
        {navItems.map((item) => {
          const Icon = item.icon
          const isActive = !item.disabled && (
            location.pathname === item.path ||
            location.pathname.startsWith(item.path + '/')
          )

          if (item.disabled) {
            return (
              <div
                key={item.path}
                className="mobile-nav-item opacity-40"
              >
                <Icon className="w-6 h-6 mb-1" />
                <span className="text-xs font-medium truncate max-w-full">{item.label}</span>
              </div>
            )
          }

          return (
            <Link
              key={item.path}
              to={item.path}
              className={clsx(
                'mobile-nav-item',
                isActive
                  ? 'mobile-nav-item-active'
                  : 'active:text-[var(--rq-text)]'
              )}
            >
              <Icon
                className={clsx(
                  'w-6 h-6 mb-1',
                  isActive && 'stroke-[2.5]'
                )}
              />
              <span
                className={clsx(
                  'text-xs truncate max-w-full',
                  isActive ? 'font-semibold' : 'font-medium'
                )}
              >
                {item.label}
              </span>
              <span className="mobile-nav-dot" />
            </Link>
          )
        })}

        {/* Search button */}
        <button
          onClick={() => setShowSearch(true)}
          className="mobile-nav-item active:text-[var(--rq-text)]"
        >
          <Search className="w-6 h-6 mb-1" />
          <span className="text-xs font-medium">Search</span>
        </button>

        {/* Logout button */}
        <button
          onClick={handleLogout}
          className="mobile-nav-item active:text-[var(--rq-text)]"
        >
          <LogOut className="w-6 h-6 mb-1" />
          <span className="text-xs font-medium">Logout</span>
        </button>
      </div>
    </nav>
    </>
  )
}

export default MobileNav
